<template>
  <!-- Las versiones nuevas del front se cargan solas (utils/pwa.js): no hay aviso de actualización. -->
  <div v-if="showConnectivity" class="pwa-status" aria-live="polite">
    <div :class="['pwa-status__notice', connectivityClass]">
      <i class="fa-solid fa-wifi pwa-status__icon" aria-hidden="true"></i>
      <span>{{ connectivityMessage }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { initPwaStatus } from '@/utils/pwa';
import {
  CONNECTIVITY_MODES,
  connectivityStatus,
  startConnectivityMonitoring,
} from '@/services/connectivity/connectivityService';
const store = useStore();
const route = useRoute();
const connectivityDismissed = ref(false);
const CONNECTIVITY_NOTICE_DURATION_MS = 5000;
let connectivityDismissTimer = null;
const localAuthorityVisible = computed(() => (
  store.state.operationalMode?.mode === 'LOCAL_MODE'
  || ['PREPARING_LOCAL', 'RECOVERY_REQUIRED', 'CLOUD_RETURN_PREPARING']
    .includes(store.state.operationalMode?.state)
  || String(store.state.operationalMode?.state || '').startsWith('RECOVERY_')
));
const isAuthenticationRoute = computed(() => (
  ['Login', 'LoginAdmin', 'ChangePass'].includes(route.name) || Boolean(route.meta.adminOnly)
));
// This banner describes the TPV/Local Hub connection. Public customer pages
// (including the QR cart) use the cloud ordering flow and must not show a
// TPV outage message over their own UI.
const isPublicCustomerRoute = computed(() => (
  [
    'Home',
    'Carta',
    'Cuenta',
    'Carrito',
    'MisPedidos',
    'LoginQr',
    'LoginDelivery',
    'LoginTakeAway',
    'LoginBarra',
    'LoginQuiosco',
  ].includes(route.name)
));

function clearConnectivityDismissTimer() {
  if (connectivityDismissTimer !== null) {
    window.clearTimeout(connectivityDismissTimer);
    connectivityDismissTimer = null;
  }
}

function scheduleConnectivityDismissal() {
  clearConnectivityDismissTimer();
  if (
    localAuthorityVisible.value
    || ![
      CONNECTIVITY_MODES.BROWSER_OFFLINE,
      CONNECTIVITY_MODES.BACKEND_UNAVAILABLE,
    ].includes(connectivityStatus.mode)
  ) return;

  connectivityDismissed.value = false;
  connectivityDismissTimer = window.setTimeout(() => {
    connectivityDismissTimer = null;
    connectivityDismissed.value = true;
  }, CONNECTIVITY_NOTICE_DURATION_MS);
}

onMounted(() => {
  initPwaStatus();
  startConnectivityMonitoring();
});

watch(
  [() => connectivityStatus.mode, localAuthorityVisible],
  ([mode]) => {
    if (mode === CONNECTIVITY_MODES.ONLINE || mode === CONNECTIVITY_MODES.CHECKING) {
      connectivityDismissed.value = false;
      clearConnectivityDismissTimer();
      return;
    }
    scheduleConnectivityDismissal();
  },
  { immediate: true },
);

onBeforeUnmount(clearConnectivityDismissTimer);

const showConnectivity = computed(() => {
  if (isAuthenticationRoute.value || isPublicCustomerRoute.value) return false;
  if (localAuthorityVisible.value) return false;
  if (connectivityDismissed.value) return false;
  if (connectivityStatus.mode === CONNECTIVITY_MODES.BROWSER_OFFLINE) return true;
  if (connectivityStatus.mode === CONNECTIVITY_MODES.CHECKING) return false;
  if (connectivityStatus.mode === CONNECTIVITY_MODES.BACKEND_UNAVAILABLE) return true;
  return false;
});
const connectivityClass = computed(() => ({
  'pwa-status__notice--offline': [
    CONNECTIVITY_MODES.BROWSER_OFFLINE,
    CONNECTIVITY_MODES.BACKEND_UNAVAILABLE,
  ].includes(connectivityStatus.mode),
  'pwa-status__notice--checking': connectivityStatus.mode === CONNECTIVITY_MODES.CHECKING,
}));
const connectivityMessage = computed(() => {
  if (connectivityStatus.mode === CONNECTIVITY_MODES.BROWSER_OFFLINE) {
    return 'Sin conexión a internet. El TPV está conectando con el Local Hub compartido.';
  }
  if (connectivityStatus.mode === CONNECTIVITY_MODES.BACKEND_UNAVAILABLE) {
    return 'EWU cloud no está disponible. El TPV está conectando con el Local Hub compartido.';
  }
  return 'Comprobando conexión con Eat with Us...';
});
</script>

<style scoped>
.pwa-status {
  position: relative;
  z-index: 2;
  width: 100%;
  padding: 8px 12px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
}

.pwa-status__notice {
  width: min(720px, 100%);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.12);
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.35;
  pointer-events: auto;
}

.pwa-status__notice--offline {
  justify-content: flex-start;
  color: #7c2d12;
  background: #fff7ed;
  border: 1px solid #fed7aa;
}

.pwa-status__notice--checking {
  justify-content: flex-start;
  color: #334155;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
}

.pwa-status__icon {
  flex: 0 0 auto;
  color: #f47700;
}

@media (max-width: 640px) {
  .pwa-status__notice {
    align-items: stretch;
    flex-direction: column;
  }

  .pwa-status__notice--offline {
    flex-direction: row;
    align-items: flex-start;
  }
}
</style>
